import { useContext, useMemo } from 'react';
import { AppContext } from '@/contexts/AppContext';
import { resolveChildName } from './resolveChildName';

/**
 * useNamedCompanionSpeech — väljer namn- eller namnlös variant av en
 * kompanjonreplik.
 *
 * Mönster: den befintliga nyckeln är den namnlösa fallbacken och en
 * syskonnyckel med suffixet `Named` bär namnvarianten, med platshållaren
 * {{childName}} (kolliderar inte med {{name}} = kompisens eget namn i
 * onboarding-repliken).
 *
 *   const { speechKey, speechVars } = useNamedCompanionSpeech('child.companion.homeIdle');
 *   useCompanionMoment('happy', speechKey, speechVars);
 *
 * Saknas ett giltigt förnamn (se resolveChildName) returneras baseKey oförändrad
 * — aldrig en tom slot, aldrig ett trunkerat namn.
 */

export type NamedCompanionSpeech = {
  /** Nyckeln att skicka till t()/useCompanionMoment. */
  speechKey: string;
  /** Interpolationsvariabler ({} när den namnlösa varianten används). */
  speechVars: Record<string, string>;
};

export const useNamedCompanionSpeech = (baseKey: string): NamedCompanionSpeech => {
  const { user } = useContext(AppContext);
  const childName = resolveChildName(user?.firstName);

  return useMemo<NamedCompanionSpeech>(() => {
    if (childName === null) {
      const noVars: Record<string, string> = {};
      return { speechKey: baseKey, speechVars: noVars };
    }
    return { speechKey: `${baseKey}Named`, speechVars: { childName } };
  }, [baseKey, childName]);
};
